import { exportCells } from '../../../../utils/table-to-excel';

/**
 * Camino de respaldo de `DownloadHistoricModal`: cuando la caché no da los ROID
 * de la selección, se leen de la tabla pintada (`#historical-<tipo>`).
 *
 * 🔴 Los índices que salen de aquí acaban en `TableToExcel`/`TableToPDF`
 * (`filterPositionColumn`, `compositeKeyColumns`), que cuentan las columnas
 * DESPUÉS de descartar las celdas con `data-export-ignore` (la bandera, spec
 * 2026-10-22 § 8.2). Aquí se cuenta igual, con la misma función: contando las
 * celdas crudas, el índice de ROID caía en la columna de al lado y el Excel y
 * el PDF de respaldo salían sin filas.
 */

/** Textos de la cabecera (primera fila del `thead`), como los cuentan los exportadores. */
function headerTexts(table: Element): string[] {
  const headerRow = table.querySelector('thead tr') as HTMLTableRowElement | null;
  if (!headerRow) return [];
  return exportCells(headerRow).map(
    (cell) => cell.textContent?.trim().toLowerCase() || ''
  );
}

/** Celdas de una fila del cuerpo, como las cuentan los exportadores. */
function bodyCells(row: Element): Element[] {
  return exportCells(row as HTMLTableRowElement);
}

/**
 * Columnas de la clave compuesta del histórico de partidas (código de partida +
 * ROID), detectadas por el texto de la cabecera. `undefined` si la tabla no
 * tiene cabecera.
 */
export function detectCompositeKeyColumns(
  table: Element
): number[] | undefined {
  if (!table.querySelector('thead tr')) return undefined;
  let budgetCodeColIdx = 2; // por defecto
  let roidColIdx = 3; // por defecto
  headerTexts(table).forEach((text, index) => {
    // Catalán «codi partida», castellano «código partida», inglés «budget item».
    if (text.includes('partida') || text.includes('budget item')) {
      budgetCodeColIdx = index;
    }
    if (text.includes('roid')) {
      roidColIdx = index;
    }
  });
  return [budgetCodeColIdx, roidColIdx];
}

/** Claves compuestas `código|ROID` de las filas pintadas, sin repetir. */
export function readCompositeKeysFromDom(
  table: Element,
  [codeColIdx, roidColIdx]: number[]
): string[] {
  const keys: string[] = [];
  table.querySelectorAll('tbody tr').forEach((row) => {
    const cells = bodyCells(row);
    const budgetCode = cells[codeColIdx]?.textContent?.trim() || '';
    const roid = cells[roidColIdx]?.textContent?.trim() || '';
    const key = `${budgetCode}|${roid}`;
    if (budgetCode && roid && !keys.includes(key)) keys.push(key);
  });
  return keys;
}

/**
 * ROID de las filas pintadas y el índice de su columna. La columna se busca por
 * el texto de la cabecera; si no aparece, se usa `fallbackIndex` (la
 * `filterPositionColumn` de la pantalla).
 */
export function readRoidsFromDom(
  table: Element,
  fallbackIndex: number
): { roidColumnIndex: number; values: string[] } {
  let roidColumnIndex = fallbackIndex;
  headerTexts(table).forEach((text, index) => {
    if (text.includes('roid')) roidColumnIndex = index;
  });

  const values: string[] = [];
  table.querySelectorAll('tbody tr').forEach((row) => {
    const text = bodyCells(row)[roidColumnIndex]?.textContent?.trim() || '';
    if (text && !values.includes(text)) values.push(text);
  });
  return { roidColumnIndex, values };
}
